import { Activity, ActivityFeedItemDefinition } from "@ofqual/portal-activity-feed";
import { ExpertsApplyModel } from "../../../../domain/ExpertsApplyFormModel";
import { AttachmentBodyProps, CaseAttachmentActivityBody, ChangeActivityBody, ExpertiseAdded, InternalMessage, PublicMessage } from "../types";
//import { fieldLabels } from "../../../Sections/fieldLabels";
import { ActivityType } from "../../../../domain/ActivityType";
import ReactMarkdown from "react-markdown";
import { Chip, Typography, Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import { authSelector } from "../../../../redux/selectors";
import { expertsApplyService } from "../../../../services";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { formatUtcDate, standardDateFormats } from "@ofqual/portal-core/core/utils/dateUtils";
import React from "react";
import { DialogType } from "../../../types";

const createRepliesText = (replies: number, lastResponseDate: string, activityType: number): React.ReactElement => {
  let val: React.ReactElement = <></>

  switch (replies) {
    case 0:
      if (activityType === ActivityType.SpecialistCitizenSendPublicMessage)
        val = <Grid spacing={1} direction="column"><Grid item><Typography variant="body2"><b><u>New message</u></b></Typography></Grid> <Grid item><Typography variant="caption">Last response on {formatUtcDate(
          lastResponseDate,
          standardDateFormats.DATE_TIME
        )}</Typography></Grid></Grid>
      else
        val = <></>;
      break;
    case 1:
      val = <Grid spacing={1} direction="column"><Grid item><Typography variant="body2"><b><u>1 reply</u></b></Typography></Grid> <Grid item><Typography variant="caption">Last response on {formatUtcDate(
        lastResponseDate,
        standardDateFormats.DATE_TIME
      )}</Typography></Grid></Grid>
      break;
    default:
      val = (
        <Grid spacing={1} direction="column">
        <Grid item><Typography variant="body2">
          <b>
            <u>{replies} replies</u>
          </b>
        </Typography></Grid>
        <Grid item><Typography variant="caption">
          Last response on{" "}
          {formatUtcDate(lastResponseDate, standardDateFormats.DATE_TIME)}
        </Typography></Grid>
        </Grid>
      );

  }

  return val;
}



export const buildDefinitions = (
  specialist: ExpertsApplyModel,
  setFocussedActivity?: (activity: Activity, activityType?: DialogType) => void
) => {

  const AttachmentBody: React.FC<AttachmentBodyProps> = ({ bodyProps }) => {
    const { body, activity } = bodyProps;
    const { token } = useSelector(authSelector);
    const buildGetBlob = (blobId: string) => () =>
      expertsApplyService.downloadActivityAttachment(
        specialist.id,
        activity.id,
        blobId,
        {
          token,
        }
      );

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((doc) => (
            <li key={doc.blobId} style={{ listStyleType: "none" }}>
              {
                <DownloadLink
                  fileName={doc.fileName}
                  getBlob={buildGetBlob(doc.blobId)}
                />
              }
            </li>
          ))}
        </ul>
      </>
    );
  };

  const messageThreadStartedByOfqual: ActivityFeedItemDefinition<PublicMessage> = {
    activityType: ActivityType.SpecialistOfqualSendPublicMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>{body.subject}</b>
            <ReactMarkdown>
              {body.message.length > 300
                ? body.message.substring(
                  0,
                  body.message.lastIndexOf(" ", 300)
                ) + "..."
                : body.message}
            </ReactMarkdown>
            {body.replies > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: "5px",
                  border: "1px solid #D3D3D3",
                  padding: "2px",
                }}
                data-test={"thread-replies-with-details-box"}
              >
                {createRepliesText(
                  body.replies,
                  body.lastMessageDate,
                  activity.activityType
                )}
                &nbsp;
                {body.messagesSentByCitizenUnreadByOfqual > 0 && (
                  <Chip
                    label={body.messagesSentByCitizenUnreadByOfqual}
                    style={{ background: "Red", color: "whitesmoke" }}
                    data-test={"chip-tag"}
                  />
                )}
              </div>
            )}
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
    options: [
      {
        label: "View",
        onClick: setFocussedActivity!,
      },
    ],
  };

  const MessageThreadStartedByCitizen: ActivityFeedItemDefinition<PublicMessage> = {
    activityType: ActivityType.SpecialistCitizenSendPublicMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>{body.subject}</b>
            <ReactMarkdown>
              {body.message.length > 300
                ? body.message.substring(
                  0,
                  body.message.lastIndexOf(" ", 300)
                ) + "..."
                : body.message}
            </ReactMarkdown>
            {(body.replies > 0 ||
              body.messagesSentByCitizenUnreadByOfqual > 0) && (
                <div
                  style={{
                    display: "inline-flex",
                    borderRadius: "5px",
                    border: "1px solid #D3D3D3",
                    padding: "2px",
                  }}
                >
                  {createRepliesText(
                    body.replies,
                    body.lastMessageDate,
                    activity.activityType
                  )}
                  &nbsp;
                  {body.messagesSentByCitizenUnreadByOfqual > 0 && (
                    <Chip
                      label={body.messagesSentByCitizenUnreadByOfqual}
                      style={{ background: "Red", color: "whitesmoke" }}
                    />
                  )}
                </div>
              )}
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
    options: [
      {
        label: "View",
        onClick: setFocussedActivity!,
      },
    ],
  };

  const noteAdded: ActivityFeedItemDefinition<CaseAttachmentActivityBody> = {
    activityType: ActivityType.SpecialistCreateNote,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Note: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const internalMessageAdded: ActivityFeedItemDefinition<InternalMessage> = {
    activityType: ActivityType.SpecialistInternalMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Recipients: </b>
            {
              <>
                {body.recipients.map((r) => (
                  <Chip label={r.name} color="secondary" />
                ))}
              </>
            }
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const smsUpdated: ActivityFeedItemDefinition<ChangeActivityBody> = {
    activityType: ActivityType.SpecialistSmsChange,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={[item.field]}
            className="ChangeChip"
            key={item.field}
          />

        ))}

      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => {
          setFocussedActivity!(activity, DialogType.SpecialistSmsChange);
        },
      },
    ],
  };

  const furtherEvidenceRequested: ActivityFeedItemDefinition<PublicMessage> = {
    activityType: ActivityType.SpecialistFurtherEvidenceRequested,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>{body.subject}</b>
            <ReactMarkdown>
              {body.message.length > 300
                ? body.message.substring(
                  0,
                  body.message.lastIndexOf(" ", 300)
                ) + "..."
                : body.message}
            </ReactMarkdown>
            {body.replies > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: "5px",
                  border: "1px solid #D3D3D3",
                  padding: "2px",
                }}
                data-test={"thread-replies-with-details-box"}
              >
                {createRepliesText(
                  body.replies,
                  body.lastMessageDate,
                  activity.activityType
                )}
                &nbsp;
                {body.messagesSentByCitizenUnreadByOfqual > 0 && (
                  <Chip
                    label={body.messagesSentByCitizenUnreadByOfqual}
                    style={{ background: "Red", color: "whitesmoke" }}
                    data-test={"chip-tag"}
                  />
                )}
              </div>
            )}
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
    options: [
      {
        label: "View",
        onClick: setFocussedActivity!,
      },
    ],
  };


  const smsExpertiseAdded: ActivityFeedItemDefinition<ExpertiseAdded> = {
    activityType: ActivityType.SpecialistAddExpertise,
    options: [
      {
        label: "View",
        onClick: (activity) => {
          setFocussedActivity!(activity, DialogType.AddExpertiseActivity);
        },
      },
    ],
  };

  return [
    messageThreadStartedByOfqual,
    MessageThreadStartedByCitizen,
    noteAdded,
    internalMessageAdded,
    smsUpdated,
    furtherEvidenceRequested,
    smsExpertiseAdded
  ];
};